import { useEffect, useRef } from "react";
import { useDialogBehavior } from "@/hooks/useDialogBehavior";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "primary";
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  cancelLabel = "Cancel",
  confirmLabel = "Confirm",
  message,
  onCancel,
  onConfirm,
  open,
  title,
  variant = "primary",
}: Readonly<ConfirmDialogProps>) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus the confirm button when opened
  useEffect(() => {
    if (open) {
      confirmRef.current?.focus();
    }
  }, [open]);

  useDialogBehavior(open, dialogRef, onCancel);

  if (!open) {
    return null;
  }

  return (
    <div className="dialog-overlay" onClick={onCancel} role="presentation">
      <div
        ref={dialogRef}
        className="dialog-box"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        aria-describedby="dialog-message"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <h3 id="dialog-title" className="dialog-title">
          {title}
        </h3>
        <p id="dialog-message" className="dialog-message">
          {message}
        </p>
        <div className="dialog-actions">
          <button onClick={onCancel}>{cancelLabel}</button>
          <button ref={confirmRef} className={variant} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
